import { statementsList, statementsAccept, statementsDismiss, type StatementImport, type StatementEffect, type LoanDriftEffect, type CardStatementFacts, type PremierFacts } from '../api-client';
import { showToast } from '../components/toast';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function fmtAed(n: number): string {
  return `AED ${n.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function cardFacts(imp: StatementImport): string {
  const p = imp.parsed as CardStatementFacts;
  const rows: string[] = [];
  if (p.totalPayable != null) rows.push(`<div class="stmt-fact"><span>Total balance</span><span>${escapeHtml(fmtAed(p.totalPayable))}</span></div>`);
  if (p.minimumDue != null) rows.push(`<div class="stmt-fact"><span>Minimum due</span><span>${escapeHtml(fmtAed(p.minimumDue))}</span></div>`);
  if (p.paymentDueDate) rows.push(`<div class="stmt-fact"><span>Due date</span><span>${escapeHtml(p.paymentDueDate)}</span></div>`);
  if (p.standingInstructionPct != null) rows.push(`<div class="stmt-fact stmt-si-line"><span>SI</span><span>${escapeHtml(`${p.standingInstructionPct}%`)}</span></div>`);
  return rows.join('');
}

function premierFacts(imp: StatementImport): string {
  const p = imp.parsed as PremierFacts;
  const rows: string[] = [];
  if (p.aedCurrent?.closingBalance != null) rows.push(`<div class="stmt-fact"><span>AED closing</span><span>${escapeHtml(fmtAed(p.aedCurrent.closingBalance))}</span></div>`);
  if (p.gbpCurrent?.closingBalance != null) {
    const gbpLine = `GBP ${p.gbpCurrent.closingBalance.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${escapeHtml(fmtAed(p.gbpCurrent.aedEquivalent))})`;
    rows.push(`<div class="stmt-fact"><span>GBP closing</span><span>${gbpLine}</span></div>`);
  }
  if (Array.isArray(p.globalMoney)) {
    for (const gm of p.globalMoney) {
      const gmLine = `${escapeHtml(gm.currency)} ${gm.balance.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${escapeHtml(fmtAed(gm.aedEquivalent))})`;
      rows.push(`<div class="stmt-fact"><span>GlobalMoney ${escapeHtml(gm.currency)}</span><span>${gmLine}</span></div>`);
    }
  }
  if (p.loan?.cashOutflow != null) rows.push(`<div class="stmt-fact"><span>Loan outflow</span><span>${escapeHtml(fmtAed(p.loan.cashOutflow))}</span></div>`);
  if (p.loan?.remainingBalance != null) rows.push(`<div class="stmt-fact"><span>Remaining</span><span>${escapeHtml(fmtAed(p.loan.remainingBalance))}</span></div>`);
  return rows.join('');
}

function renderEffect(effect: StatementEffect): string {
  if (effect.kind === 'occurrence_upsert') {
    const verb = effect.exists ? 'Update occurrence' : 'Create occurrence';
    return `<div class="stmt-effect">
      <span class="stmt-effect-label">${verb}</span>
      <span>Due ${escapeHtml(effect.dueDate)} — ${escapeHtml(fmtAed(effect.statementBalance))} / min ${escapeHtml(fmtAed(effect.minimumDue))}</span>
    </div>`;
  }
  if (effect.kind === 'si_warning') {
    return `<div class="stmt-effect stmt-effect-warning">
      <span>⚠ ${escapeHtml(effect.message)}</span>
    </div>`;
  }
  if (effect.kind === 'snapshot') {
    if (effect.action === 'skip_fresher') {
      return `<div class="stmt-effect stmt-effect-skip">
        <span class="stmt-effect-label">Snapshot skipped</span>
        <span>Kept existing — fresher (as of ${escapeHtml(effect.existingAsOf ?? '?')})</span>
      </div>`;
    }
    return `<div class="stmt-effect">
      <span class="stmt-effect-label">Create snapshot</span>
      <span>${escapeHtml(fmtAed(effect.balance))} as of ${escapeHtml(effect.asOf)}</span>
    </div>`;
  }
  if (effect.kind === 'loan_drift') {
    // rendered separately via loan drift block
    return '';
  }
  return '';
}

function renderLoanDrift(effect: LoanDriftEffect): string {
  const opts = effect.candidates.map((c) =>
    `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)} (${escapeHtml(fmtAed(c.amount))})</option>`
  ).join('');
  const select = effect.candidates.length > 1
    ? `<select class="stmt-loan-select">${opts}</select>`
    : '';
  return `<div class="stmt-effect stmt-loan-drift">
    <label class="stmt-loan-label">
      <input type="checkbox" class="stmt-loan-check" checked>
      Update loan commitment to ${escapeHtml(fmtAed(effect.bankAmount))}
    </label>
    ${select}
  </div>`;
}

function renderEmptyState(list: HTMLElement): void {
  list.innerHTML = `<p class="muted">Nothing to review.</p>`;
}

function buildImportEl(imp: StatementImport, list: HTMLElement): HTMLElement {
  const el = document.createElement('div');
  el.className = 'stmt-row';
  el.dataset.id = imp.id;

  const typeBadge = imp.statementType === 'hsbc_card' ? 'Card statement' : 'Premier statement';
  const facts = imp.statementType === 'hsbc_card' ? cardFacts(imp) : premierFacts(imp);

  const driftEffect = imp.proposedEffects.find((e): e is LoanDriftEffect => e.kind === 'loan_drift');
  const effectsHtml = imp.proposedEffects
    .filter((e) => e.kind !== 'loan_drift')
    .map(renderEffect)
    .join('');
  const driftHtml = driftEffect ? renderLoanDrift(driftEffect) : '';

  el.innerHTML = `
    <div class="stmt-header">
      <span class="stmt-badge">${escapeHtml(typeBadge)}</span>
      <span class="stmt-date">${escapeHtml(imp.statementDate)}</span>
      <span class="stmt-file muted">${escapeHtml(imp.fileName)}</span>
    </div>
    <div class="stmt-facts">${facts}</div>
    <div class="stmt-effects">${effectsHtml}${driftHtml}</div>
    <div class="stmt-actions">
      <button class="stmt-dismiss ghost">Dismiss</button>
      <button class="stmt-accept">Accept</button>
    </div>`;

  const acceptBtn = el.querySelector<HTMLButtonElement>('.stmt-accept')!;
  const dismissBtn = el.querySelector<HTMLButtonElement>('.stmt-dismiss')!;

  acceptBtn.onclick = async () => {
    acceptBtn.disabled = true;
    dismissBtn.disabled = true;
    try {
      const opts: { updateLoanCommitment?: boolean; loanCommitmentId?: string } = {};
      if (driftEffect) {
        const check = el.querySelector<HTMLInputElement>('.stmt-loan-check');
        const isChecked = check?.checked ?? false;
        if (isChecked) {
          const sel = el.querySelector('.stmt-loan-select') as HTMLSelectElement | null;
          const candidateId = sel ? sel.value : driftEffect.candidates[0]?.id;
          if (candidateId) {
            opts.updateLoanCommitment = true;
            opts.loanCommitmentId = candidateId;
          }
        }
      }
      await statementsAccept(imp.id, opts);
      el.remove();
      if (list.children.length === 0) {
        renderEmptyState(list);
      }
    } catch (err) {
      acceptBtn.disabled = false;
      dismissBtn.disabled = false;
      showToast(err instanceof Error ? err.message : 'Failed to accept — please try again', 'error');
    }
  };

  dismissBtn.onclick = async () => {
    try {
      await statementsDismiss(imp.id);
      el.remove();
      if (list.children.length === 0) {
        renderEmptyState(list);
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to dismiss — please try again', 'error');
    }
  };

  return el;
}

export async function renderStatements(root: HTMLElement): Promise<void> {
  root.innerHTML = `
    <header class="page-header">
      <h1>Statements</h1>
    </header>
    <main>
      <div class="stmt-list"></div>
    </main>`;
  const list = root.querySelector<HTMLElement>('.stmt-list')!;

  const { imports } = await statementsList('pending');

  if (imports.length === 0) {
    renderEmptyState(list);
    return;
  }

  for (const imp of imports) {
    list.appendChild(buildImportEl(imp, list));
  }
}
